/**
 * QuickMenu — the player-facing quick menu, bottom-right (≡).
 *
 * Rebuild of the legacy MicroMenu (deleted 2026-06-12): entries DERIVE from
 * the window registry's `game` category (labels/icons/ids are the engine's —
 * no parallel button config to drift), hotkey hints read the LIVE bindings
 * from KeyBindingManager (the old menu advertised phantom keys), open-state
 * dots come from the engine, and the styling sits on the theme tokens.
 *
 * Collapsed by default to a small ≡ launcher; expands into a vertical list.
 * Lobby-gated windows (Lobby Tools) only render on lobby maps — that gate
 * lives HERE (LobbyToolsPanel does not self-gate; the server enforces too).
 */

import React, { useEffect, useState } from 'react';
import { DevConfig } from '../../../../config/environment/devConfig';
import { HUD_PANEL_DEFAULTS } from '../../../../config/ui/hudDefaults.config';
import {
  QUICK_MENU_ORDER,
  QUICK_MENU_BINDING_ACTION,
  QUICK_MENU_HIDDEN,
  QUICK_MENU_LOBBY_ONLY,
} from '../../../../config/ui/quickMenu.config';
import { isLobbyMap } from '../../../../shared/constants/LobbyTools';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { KeyBindingManager } from '../../../services/KeyBindingManager';
import { useWindows } from '../../framework/WindowManagerContext';
import type { WindowDef } from '../../framework/types';
import { useInputModality } from '../../hooks/useInputModality';
import { useFullscreen } from '../../hooks/useFullscreen';
import { useControllerNavigation } from '../../input/controller/ControllerNavigationContext';
import { InputHint } from '../../primitives/InputHint';
import { COLORS, SPACE, RADII, SHADOW, ACCENT, INK } from '../../theme';
import type { InputSystem } from '../../../systems/input/InputSystem';

interface QuickMenuProps {
  /** Current map id — drives the lobby-only gate (Lobby Tools). */
  currentMapId?: string;
  /**
   * Compact landscape cap (W2a, 2026-09-28): max border-box height of the
   * EXPANDED panel. When set, the shell becomes a flex column and the list
   * scrolls inside the cap instead of overgrowing the viewport (the base
   * max-height is the list's 60vh). Collapsed launcher unaffected.
   */
  maxHeight?: number;
  /**
   * Compact landscape width (W2a-2, 2026-09-28): border-box width of the
   * EXPANDED panel. The compact derivation clamps it to the column BETWEEN the
   * left HUD column and the measured minimap so the panel stays fully visible
   * at 640 px landscape (196 → 162). Collapsed launcher unaffected.
   */
  width?: number;
  /** Unspent stat points for badge notifications (Plan 51 Task 3.1) */
  statPoints?: number;
  /** Unspent talent points for badge notifications (Plan 51 Task 3.1) */
  skillPoints?: number;
}

export const QuickMenu: React.FC<QuickMenuProps> = ({
  currentMapId,
  maxHeight,
  width,
  statPoints = 0,
  skillPoints = 0,
}) => {
  const { windows, widgets } = useWindows();
  const {
    quickMenuOpen: isExpanded,
    openQuickMenu,
    closeQuickMenu,
    activateWindow,
  } = useControllerNavigation();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
  const { modality, controller } = useInputModality();
  const menuBinding = controller?.bindings.menu;
  const confirmBinding = controller?.bindings.jump;
  const backBinding = controller?.bindings.secondaryAttack;

  // Auto-Walk checkbox: InputSystem owns the latch and notifies on change.
  const getInputSystem = () => ServiceRegistry.getInstance().get('InputSystem', { optional: true }) as InputSystem | undefined;
  const [autowalkActive, setAutowalkActive] = useState<boolean>(() => getInputSystem()?.isAutowalkActive() ?? false);

  useEffect(() => {
    const input = isExpanded ? getInputSystem() : undefined;
    if (!input) return;
    setAutowalkActive(input.isAutowalkActive());
    return input.onAutowalkChange(() => setAutowalkActive(input.isAutowalkActive()));
  }, [isExpanded]);

  const handleToggleAutowalk = () => {
    const next = getInputSystem()?.toggleAutowalk();
    if (next !== undefined) setAutowalkActive(next);
  };

  if (!isExpanded) {
    const hasUnspent = statPoints > 0 || skillPoints > 0;
    return (
      <button
        type="button"
        className="quick-menu-launcher"
        data-testid="quick-menu-launcher"
        data-controller-quick-menu-launcher
        data-input-modality={modality}
        onClick={openQuickMenu}
        title={hasUnspent
          ? `Quick menu (${statPoints > 0 ? `${statPoints} stat pts` : ''}${statPoints > 0 && skillPoints > 0 ? ', ' : ''}${skillPoints > 0 ? `${skillPoints} talent pts` : ''})`
          : 'Quick menu'}
        aria-label="Open quick menu"
        style={launcherStyle}
      >
        <span aria-hidden>≡</span>
        {hasUnspent && (
          <span
            data-testid="quick-menu-alert-pip"
            aria-label="Unspent points available"
            style={launcherPipStyle}
          />
        )}
        {modality === 'gamepad' && menuBinding?.label && (
          <InputHint label={menuBinding.label} device="gamepad" />
        )}
      </button>
    );
  }

  // Derive entries from the registry's game category, in player order;
  // unlisted game windows append (a new game window can't silently vanish).
  const game = windows.filter((w) => w.category === 'game');
  const ordered: WindowDef[] = [
    ...QUICK_MENU_ORDER.map((id) => game.find((w) => w.id === id)).filter(
      (w): w is WindowDef => !!w,
    ),
    ...game.filter((w) => !QUICK_MENU_ORDER.includes(w.id)),
  ];
  const lobbyToolsEnabled = DevConfig.lobbyToolsEnabled !== false;
  const visible = ordered.filter((w) => {
    if (QUICK_MENU_HIDDEN.has(w.id)) return false;
    return QUICK_MENU_LOBBY_ONLY.has(w.id)
      ? lobbyToolsEnabled && isLobbyMap(currentMapId)
      : true;
  });
  const bindings = KeyBindingManager.getInstance();
  const expandedShellStyle: React.CSSProperties = {
    ...shellStyle,
    ...(width !== undefined ? { width } : null),
    ...(maxHeight !== undefined
      ? { display: 'flex', flexDirection: 'column', maxHeight }
      : null),
  };

  return (
    <div
      style={expandedShellStyle}
      data-testid="quick-menu"
      data-controller-surface="quick-menu"
      data-input-modality={modality}
    >
      <div style={headerStyle}>
        <span style={headerLabelStyle}>
          Menu
          {modality === 'gamepad' && (
            <span
              aria-label="Controller input active"
              data-testid="quick-menu-gamepad-active"
              title="Controller was last used"
              style={gamepadActiveStyle}
            >
              🎮
            </span>
          )}
          {modality === 'gamepad' && menuBinding?.label && (
            <InputHint label={menuBinding.label} device="gamepad" />
          )}
        </span>
        <button
          type="button"
          style={collapseStyle}
          onClick={closeQuickMenu}
          title="Collapse quick menu"
          aria-label="Close quick menu"
        >
          ×
        </button>
      </div>
      <div
        data-testid="quick-menu-list"
        style={maxHeight !== undefined
          ? { ...listStyle, maxHeight: 'none', flex: '1 1 auto', minHeight: 0 }
          : listStyle}
      >
        {visible.map((w) => {
          const action = QUICK_MENU_BINDING_ACTION[w.id];
          const key = action ? bindings.getKeyForAction(action) : undefined;
          const open = widgets[w.id]?.enabled ?? false;
          const unspentCount = w.id === 'equipment' ? statPoints : w.id === 'talents' ? skillPoints : 0;
          return (
            <button
              key={w.id}
              type="button"
              data-testid={`quick-menu-${w.id.toLowerCase()}`}
              data-controller-window-launcher={w.id}
              onClick={() => activateWindow(w.id, open)}
              title={w.description}
              style={{
                ...rowStyle,
                background: open ? 'rgba(80, 130, 200, 0.22)' : 'transparent',
                borderColor: open ? COLORS.info : 'transparent',
              }}
            >
              <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>{w.icon}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{w.label}</span>
              {unspentCount > 0 && (
                <span
                  data-testid={`quick-menu-${w.id}-unspent`}
                  style={{
                    padding: '1px 5px',
                    borderRadius: 8,
                    fontSize: 9.5,
                    fontWeight: 700,
                    marginRight: key && modality === 'keyboard-pointer' ? SPACE.xs : 0,
                    background: w.id === 'talents' ? 'rgba(40, 180, 100, 0.25)' : 'rgba(220, 160, 40, 0.25)',
                    color: w.id === 'talents' ? '#70ffa0' : '#ffd700',
                    border: `1px solid ${w.id === 'talents' ? 'rgba(60, 220, 140, 0.6)' : 'rgba(240, 190, 60, 0.6)'}`,
                  }}
                >
                  +{unspentCount}
                </span>
              )}
              {/* There are no controller shortcuts for these windows yet.
                  In gamepad mode, omit the keyboard-only chip instead of
                  advertising a button that does not route anywhere. */}
              {key && modality === 'keyboard-pointer' && (
                <InputHint label={key} device="keyboard" />
              )}
            </button>
          );
        })}
        <button
          type="button"
          role="menuitemcheckbox"
          aria-checked={autowalkActive}
          data-testid="quick-menu-autowalk"
          onClick={handleToggleAutowalk}
          title="Toggle continuous forward movement (Auto-Walk)"
          style={{
            ...rowStyle,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            marginTop: 2,
            background: autowalkActive ? 'rgba(80, 180, 120, 0.22)' : 'transparent',
            borderColor: autowalkActive ? 'rgba(112, 255, 160, 0.55)' : 'transparent',
          }}
        >
          <span
            aria-hidden
            data-testid="quick-menu-autowalk-checkbox"
            style={{
              width: 16,
              height: 16,
              marginLeft: 2,
              marginRight: 2,
              borderRadius: 3,
              border: `1.5px solid ${autowalkActive ? '#70ffa0' : ACCENT.brass}`,
              background: autowalkActive ? 'rgba(60, 200, 120, 0.35)' : 'rgba(0, 0, 0, 0.35)',
              color: '#70ffa0',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {autowalkActive ? '✓' : ''}
          </span>
          <span style={{ flex: 1, textAlign: 'left' }}>Auto-Walk</span>
          {(() => {
            const autowalkKey = bindings.getKeyForAction('autowalk');
            return autowalkKey && modality === 'keyboard-pointer' ? (
              <InputHint label={autowalkKey} device="keyboard" />
            ) : null;
          })()}
        </button>
        <button
          type="button"
          data-testid="quick-menu-fullscreen"
          onClick={() => {
            toggleFullscreen();
            closeQuickMenu();
          }}
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          style={rowStyle}
        >
          <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>
            {isFullscreen ? '⤦' : '⛶'}
          </span>
          <span style={{ flex: 1, textAlign: 'left' }}>
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </span>
        </button>
      </div>
      {modality === 'gamepad' && (confirmBinding?.label || backBinding?.label) && (
        <div style={controllerHelpStyle} aria-label="Controller menu actions">
          {confirmBinding?.label && (
            <span style={controllerHelpItemStyle}>
              <InputHint label={confirmBinding.label} device="gamepad" /> Select
            </span>
          )}
          {backBinding?.label && (
            <span style={controllerHelpItemStyle}>
              <InputHint label={backBinding.label} device="gamepad" /> Back
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ── styles (theme-tokened) ────────────────────────────────────────────

const launcherStyle: React.CSSProperties = {
  position: 'relative',
  minWidth: 34,
  height: 34,
  // Mini ink well + brass fitting (HUD-restyle §2).
  background: `linear-gradient(180deg, ${INK.well}, ${INK.wellLo})`,
  border: `1px solid ${ACCENT.brass}`,
  borderRadius: RADII.md,
  color: COLORS.textSecondary,
  fontSize: 16,
  cursor: 'pointer',
  pointerEvents: 'auto',
  boxShadow: `${SHADOW.sm}, inset 0 2px 4px rgba(0, 0, 0, 0.4)`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: SPACE.xs,
  padding: `0 ${SPACE.xs}px`,
};

const launcherPipStyle: React.CSSProperties = {
  position: 'absolute',
  top: -3,
  right: -3,
  width: 9,
  height: 9,
  borderRadius: '50%',
  backgroundColor: '#ffd700',
  border: '1.5px solid #1a1a1a',
  boxShadow: '0 0 6px #ffd700',
  pointerEvents: 'none',
};

const shellStyle: React.CSSProperties = {
  width: HUD_PANEL_DEFAULTS.quickMenu.width,
  background: `linear-gradient(180deg, ${INK.hi}, ${INK.base} 55%, ${INK.lo})`,
  border: `1px solid ${ACCENT.brass}`,
  borderRadius: RADII.lg,
  boxShadow: SHADOW.md,
  padding: SPACE.sm,
  pointerEvents: 'auto',
  fontFamily: 'Inter, sans-serif',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${SPACE.xxs}px ${SPACE.xs}px ${SPACE.xs}px`,
  borderBottom: `1px solid ${COLORS.borderSubtle}`,
  marginBottom: SPACE.xs,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: COLORS.textMuted,
};

const headerLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: SPACE.xs,
};

const gamepadActiveStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLORS.info,
  fontSize: 11,
  lineHeight: 1,
};

const collapseStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: COLORS.textMuted,
  cursor: 'pointer',
  fontSize: 12,
  padding: '0 2px',
};

const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  maxHeight: '60vh',
  overflowY: 'auto',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.sm,
  padding: `${SPACE.xs}px ${SPACE.sm}px`,
  border: '1px solid transparent',
  borderRadius: RADII.md,
  color: COLORS.textPrimary,
  fontSize: 12,
  cursor: 'pointer',
  transition: 'background 0.1s',
};

const controllerHelpStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: SPACE.sm,
  padding: `${SPACE.xs}px ${SPACE.xs}px 0`,
  marginTop: SPACE.xs,
  borderTop: `1px solid ${COLORS.borderSubtle}`,
  color: COLORS.textMuted,
  fontSize: 9,
};

const controllerHelpItemStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: SPACE.xxs,
};
